'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import Markdown from 'react-markdown'
import { cn } from '../../../lib/cn'
import {
  sectionVariants,
  slideFromLeftVariants,
  slideFromRightVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../../lib/motion'
import { GlowCenter } from '../../backgrounds/glow/glow-center'

/**
 * Performance - Split layout with text left, stats grid right
 *
 * Features:
 * - Two-tone heading support (main text + accent text)
 * - Stats grid with semi-transparent cards
 * - Labels in primary/accent color
 * - Responsive: stacks on mobile, side-by-side on desktop
 */
export interface PerformanceStat {
  /** Large value displayed prominently (e.g., "100K-500K", "90%") */
  value: string
  /** Small label below value (e.g., "TPS", "Latency") */
  label: string
}

export interface PerformanceProps {
  /** Main heading text */
  heading?: string
  /** Accent-colored heading text (displayed after main heading) */
  headingAccent?: string
  /** Description paragraph */
  description?: string
  /** Stats to display in the grid */
  stats?: PerformanceStat[]
  /** Optional className for the section */
  className?: string
}

const defaultStats: PerformanceStat[] = [
  { value: '8', label: 'Database Interfaces' },
  { value: '$20-50', label: 'Monthly Cost' },
  { value: '$0', label: 'Egress Fees' },
  { value: '0', label: 'Services to Manage' },
  { value: '10GB', label: 'Free Storage' },
]

const Performance = ({
  heading = 'One Schema.',
  headingAccent = 'Every Database.',
  description = 'Stop paying for seven services when one will do. IceType unifies transactions, documents, key-value, streams, graph, search, and vectors—all from a single schema definition.',
  stats = defaultStats,
  className,
}: PerformanceProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const contentAnimation = prefersReducedMotion ? reducedMotionVariants : slideFromLeftVariants
  const statsAnimation = prefersReducedMotion ? reducedMotionVariants : slideFromRightVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('relative overflow-hidden py-16 sm:py-24 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left: Text content */}
          <motion.div
            variants={contentAnimation}
            className="flex flex-col lg:items-start lg:text-left"
          >
            <h2 className="mb-6 text-3xl font-semibold tracking-tight sm:text-4xl">
              {heading && <span>{heading}</span>}
              {heading && headingAccent && <br />}
              {headingAccent && <span className="text-primary">{headingAccent}</span>}
            </h2>
            {description && (
              <div className="max-w-xl text-muted-foreground prose prose-sm max-w-none prose-p:text-muted-foreground prose-strong:text-foreground">
                <Markdown>{description}</Markdown>
              </div>
            )}
          </motion.div>

          {/* Right: Stats grid - bento style with varied column spans */}
          <div className="relative">
            <GlowCenter animate={true} />
            <motion.div
              variants={statsAnimation}
              className={cn(
                'relative z-10 grid gap-3',
                // Grid structure based on stat count
                stats.length === 3 && 'grid-cols-1 sm:grid-cols-3',
                stats.length === 4 && 'grid-cols-2',
                stats.length === 5 && 'grid-cols-2 sm:grid-cols-6',
                stats.length === 6 && 'grid-cols-2 sm:grid-cols-3',
                stats.length === 7 && 'grid-cols-2 sm:grid-cols-12'
              )}
            >
              {stats.map((stat, i) => {
                // Calculate column span for bento layouts
                let colSpan = ''
                if (stats.length === 5) {
                  // Mobile: first card full width, then 2x2 grid
                  // Desktop: first 2 span 3 cols, last 3 span 2 cols
                  if (i === 0) {
                    colSpan = 'col-span-2 sm:col-span-3'
                  } else if (i === 1) {
                    colSpan = 'sm:col-span-3'
                  } else {
                    colSpan = 'sm:col-span-2'
                  }
                } else if (stats.length === 7) {
                  // First 4: span 3 cols each (3*4=12)
                  // Last 3: span 4 cols each (4*3=12)
                  colSpan = i < 4 ? 'sm:col-span-3' : 'sm:col-span-4'
                }

                return (
                  <motion.div
                    key={stat.label}
                    variants={itemAnimation}
                    className={cn(
                      'rounded-[var(--radius)] border border-border/40 bg-foreground/[0.03] p-4 sm:p-5',
                      colSpan
                    )}
                  >
                    <div className="text-2xl font-base tracking-tight">
                      {stat.value}
                    </div>
                    <div className="sm:mt-2 text-sm text-primary/80">
                      {stat.label}
                    </div>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>
  )
}

export { Performance }
